import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Info, Check, Loader2 } from "lucide-react";
import axios from "axios";
import { getTrackedOperatorId } from "../utils/operatorTracking";
import { SchoolLookup } from "./SchoolLookup";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const InfoRequestModal = ({ open, onOpenChange }) => {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", ico: "", school_name: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.name.trim() || !formData.email.trim() || !formData.phone.trim() || !formData.school_name.trim()) {
      setError("Prosím vyplňte jméno, email, telefon a název školy.");
      return;
    }
    if (!formData.email.includes("@")) {
      setError("Prosím zadejte platný email.");
      return;
    }

    setLoading(true);
    try {
      const trackedOp = getTrackedOperatorId();
      const submitData = { ...formData };
      if (trackedOp) submitData.operator_id = trackedOp;
      await axios.post(`${API}/info-request`, submitData);
      setSuccess(true);
    } catch (err) {
      setError("Něco se nepovedlo. Zkuste to prosím znovu.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(() => {
      setFormData({ name: "", email: "", phone: "", ico: "", school_name: "", message: "" });
      setSuccess(false);
      setError("");
    }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md rounded-3xl border-[#E8EDF5] p-0 overflow-hidden">
        <div className="bg-gradient-to-br from-[#0049B3] to-[#003380] p-6 pb-8">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl bg-[#FFC72C]/20 flex items-center justify-center">
              <Info className="w-5 h-5 text-[#FFC72C]" />
            </div>
            <DialogHeader className="text-left space-y-0">
              <DialogTitle className="text-xl font-bold text-white" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                Chci více informací
              </DialogTitle>
              <DialogDescription className="text-blue-200/60 text-sm">
                Nechte nám kontakt, ozveme se vám
              </DialogDescription>
            </DialogHeader>
          </div>
        </div>

        <div className="p-6">
          {success ? (
            <div className="text-center py-6">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-[#0F1B3D] mb-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                Děkujeme!
              </h3>
              <p className="text-[#4A5B7A] text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>
                Brzy se vám ozveme s dalšími informacemi o Agentovi ŠVP.
              </p>
              <Button className="mt-6 bg-[#0049B3] hover:bg-[#003380] text-white rounded-xl font-semibold px-6" onClick={handleClose}>
                Zavřít
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                placeholder="Vaše jméno"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="rounded-xl border-[#E8EDF5] py-5 focus:border-[#0049B3] focus:ring-[#0049B3]/20"
              />
              <Input
                type="email"
                placeholder="Váš email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="rounded-xl border-[#E8EDF5] py-5 focus:border-[#0049B3] focus:ring-[#0049B3]/20"
              />
              <Input
                type="tel"
                placeholder="Telefon"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="rounded-xl border-[#E8EDF5] py-5 focus:border-[#0049B3] focus:ring-[#0049B3]/20"
              />
              <div>
                <label className="text-xs font-semibold text-[#3D4F6F] mb-1 block">Škola (hledejte podle IČO nebo názvu)</label>
                <SchoolLookup onSelect={({ ico, school_name }) => setFormData({ ...formData, ico, school_name })} />
              </div>
              <Textarea
                placeholder="Zpráva (nepovinné)"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="rounded-xl border-[#E8EDF5] focus:border-[#0049B3] focus:ring-[#0049B3]/20 min-h-[80px]"
              />
              {error && <p className="text-sm text-red-500 font-medium">{error}</p>}
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#FFC72C] hover:bg-[#E5B326] text-[#1A1A2E] rounded-xl font-bold py-5 text-base shadow-lg shadow-amber-400/20"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Odeslat"}
              </Button>
              <p className="text-xs text-center text-[#9CA3AF]">Vaše údaje jsou v bezpečí.</p>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
